import { computed, Ref, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  IInvoice,
  IInvoiceData,
  IInvoiceItem,
  IInvoiceItemData,
  Invoice,
  InvoiceItem,
  ITax
} from '@/models';
import { IFetchInvoicesParams } from '@/services/invoice';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import moment from 'moment';
import { provider } from '@/services';

export interface UpdateInvoiceStatePayload {
  id: number;
  state: number;
}

export function useTaxTextVisuals(lineItem: MaybeRef<IInvoiceItem[]>) {
  const invoiceItems = ref(lineItem);
  // Create a computed property to react to changes
  const taxes = computed(() => {
    const result: Record<string, number> = {};

    if (!invoiceItems.value) return result;

    (invoiceItems.value as IInvoiceItem[]).forEach(item => {
      if (item.tax) {
        const key = `${(item.tax as ITax).name} (${(item.tax as ITax).rate}%)`;
        if (result[key]) {
          result[key] += item.tax_amount;
        } else {
          result[key] = item.tax_amount;
        }
      }
    });

    return result;
  });

  return taxes;
}

export function useFetchInvoices(filter?: IFetchInvoicesParams) {
  const queryKey = ref(['invoice', filter]);

  const count = ref<number>(0);

  const { isLoading, isError, error, data: invoices } = useQuery({
    queryKey: queryKey,
    queryFn: () => provider().invoice.fetchInvoices(filter!),
    enabled: computed(() => Boolean(filter)),
    select: data => {
      count.value = data.count;
      return data.results.map(invoice => new Invoice(invoice));
    }
  });

  return { queryKey, invoices, count, error, isError, isLoading };
}

export function useFetchInvoice(id: MaybeRef<number>) {
  const invoiceIdRef = ref(id);

  const queryKey = ref(['invoice', invoiceIdRef]);

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(invoiceIdRef.value)),
    queryFn: () => provider().invoice.fetchInvoice(invoiceIdRef.value),
    select: data => new Invoice(data)
  });

  return { invoice, isLoading, error, queryKey };
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-invoice'],
    mutationFn: (invoice: IInvoiceData) =>
      provider().invoice.createInvoice(invoice),

    onSuccess: data => {
      queryClient.setQueryData(['invoice', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['invoice']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-invoice'],
    mutationFn: (invoice: IInvoiceData) =>
      provider().invoice.updateInvoice(invoice),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['invoice', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['invoice']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteInvoice() {
  const mutationKey = ref(['delete-invoice']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) => provider().invoice.deleteInvoice(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['invoice']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useUpdateInvoiceState() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-invoice-state'],
    mutationFn: (payload: UpdateInvoiceStatePayload) =>
      provider().invoice.updateInvoiceState(payload.id, payload.state),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['invoice', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['invoice']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useExportInvoicePdf() {
  //use invoiceRef to store the invoice object to be used in the onSuccess callback
  const invoiceRef = ref<IInvoice>();

  const { isLoading, isError, error, mutate, reset } = useMutation({
    mutationKey: ['export-invoice-pdf'],
    mutationFn: (invoice: IInvoice) => {
      invoiceRef.value = invoice;
      return provider().invoice.exportInvoice(invoice.id!);
    },

    onSuccess: data => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `invoice-${invoiceRef.value!.number}-${moment(new Date()).format(
          'YYYY-MM-DD-HH-mm-ss'
        )}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  });
  return { isLoading, isError, error, mutate, reset };
}

//InvoiceItem
export function useFetchInvoiceItems(id: MaybeRef<number>) {
  const invoiceIdRef = ref(id);
  const queryKey = ref(['invoice-item', invoiceIdRef]);

  const count = ref<number>(0);

  const { isLoading, isError, error, data: invoiceItems } = useQuery({
    queryKey: queryKey,
    queryFn: () => provider().invoice.fetchInvoiceItems(invoiceIdRef.value),
    enabled: computed(() => Boolean(invoiceIdRef.value)),
    select: data => {
      return data.map(invoiceItem => new InvoiceItem(invoiceItem));
    }
  });

  return { queryKey, invoiceItems, count, error, isError, isLoading };
}

export function useFetchInvoiceItem(
  invoiceId: MaybeRef<number>,
  id: MaybeRef<number>
) {
  const invoiceIdRef = ref(invoiceId);
  const invoiceItemIdRef = ref(id);

  const queryKey = ref(['invoice-item', invoiceItemIdRef]);

  const { data: invoiceItem, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(invoiceItemIdRef.value)),
    queryFn: () =>
      provider().invoice.fetchInvoiceItem(
        invoiceIdRef.value,
        invoiceItemIdRef.value
      ),
    select: data => new InvoiceItem(data)
  });

  return { invoiceItem, isLoading, error, queryKey };
}

export function useCreateInvoiceItem(invoiceId: MaybeRef<number>) {
  const invoiceIdRef = ref(invoiceId);
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-invoice-item'],
    mutationFn: (invoiceItem: IInvoiceItemData) =>
      provider().invoice.createInvoiceItem(invoiceIdRef.value, invoiceItem),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['invoice', invoiceIdRef.value])
      });
      queryClient.setQueryData(['invoice-item', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['invoice-item'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateInvoiceItem(invoiceId: MaybeRef<number>) {
  const invoiceIdRef = ref(invoiceId);
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-invoice-item'],
    mutationFn: (invoiceItem: IInvoiceItemData) =>
      provider().invoice.updateInvoiceItem(invoiceIdRef.value, invoiceItem),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['invoice', invoiceIdRef.value])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['invoice-item', data.id])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['invoice-item'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteInvoiceItem(invoiceId: MaybeRef<number>) {
  const invoiceIdRef = ref(invoiceId);
  const mutationKey = ref(['delete-invoice-item']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      provider().invoice.deleteInvoiceItem(invoiceIdRef.value, id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['invoice', invoiceIdRef.value])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['invoice-item'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
